import React from "react";
import "./LumiOrb.css";

export const LUMI_STATES = {
  IDLE: "idle",
  LISTENING: "listening",
  THINKING: "thinking",
  CELEBRATING: "celebrating",
  STUCK: "stuck",
  CURIOUS: "curious",
  WRONG: "wrong",
  PROCESSING: "processing",
};

// Mood -> glow ring colour. States not listed here (idle) fall back to the
// subject accent so the ring is ambient between transient mood triggers.
// Source: "Lumio Logo.dc.html" canonical rule — guide/think/nudge/win.
const MOOD_RING = {
  listening: "#5B6BD6",  // think
  thinking:  "#5B6BD6",  // think
  processing:"#5B6BD6",  // think
  curious:   "#E8A24A",  // guide
  stuck:     "#E8A24A",  // guide
  wrong:     "#F0A500",  // nudge
  celebrating:"#16A36B", // win
};

// Same subject accent palette used across the Flight Deck redesign
// (year3/index.json subject defs in lumio.jsx).
const SUBJECT_RING = {
  mathematics: "#6C5CE7",
  science:     "#16A36B",
  english:     "#FF7A59",
  hass:        "#3B82F6",
  technologies:"#C2551F",
};

const DEFAULT_RING = "#6C5CE7";

function ringColorFor(state, subject) {
  return MOOD_RING[state] || SUBJECT_RING[subject] || DEFAULT_RING;
}

export function LumiOrb({ state = "idle", subject, size = 64, onClick, className = "" }) {
  const ringColor = ringColorFor(state, subject);

  return (
    <div
      className={`lumiorb lumiorb-${state}${className ? " " + className : ""}`}
      style={{
        position: "relative",
        width: size,
        height: size,
        cursor: onClick ? "pointer" : "default",
      }}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      aria-label={`Lumi — ${state}`}
    >
      {/* Glow ring — mood when active, subject accent when idle. Never the body. */}
      <div
        className="lumiorb-ring"
        style={{
          position: "absolute",
          inset: -Math.round(size * 0.12),
          borderRadius: "50%",
          border: `${Math.max(2, Math.round(size * 0.038))}px solid ${ringColor}`,
          boxShadow: `0 0 ${Math.round(size * 0.24)}px ${ringColor}`,
        }}
      />

      {/* Body — fixed violet gradient identity, matches the Empirio orb lockup everywhere else */}
      <div
        className="lumiorb-body"
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: "50%",
          background: "radial-gradient(circle at 34% 28%,#C7BEF6,#6C5CE7 58%,#3A2E8C)",
          boxShadow: `0 ${Math.round(size * 0.15)}px ${Math.round(size * 0.4)}px rgba(108,92,231,.4), inset 0 -${Math.round(size * 0.1)}px ${Math.round(size * 0.26)}px rgba(42,28,120,.4)`,
        }}
      />
      <div
        className="lumiorb-sheen"
        style={{
          position: "absolute",
          inset: Math.round(size * 0.1),
          borderRadius: "50%",
          background: "conic-gradient(from 0deg,transparent,rgba(255,255,255,.4),transparent 42%)",
          mixBlendMode: "screen",
          opacity: 0.7,
        }}
      />
      <div
        className="lumiorb-highlight"
        style={{
          position: "absolute",
          left: "22%",
          top: "20%",
          width: size * 0.29,
          height: size * 0.18,
          borderRadius: "50%",
          background: "radial-gradient(circle,rgba(255,255,255,.9),transparent 70%)",
          filter: "blur(1px)",
        }}
      />
      <div className="lumiorb-eyes" style={{ position: "absolute", left: 0, top: "45%", width: "100%", display: "flex", justifyContent: "center", gap: Math.round(size * 0.14) }}>
        <span style={{ width: size * 0.11, height: size * 0.2, borderRadius: "50%", background: "#fff", boxShadow: "0 0 8px rgba(255,255,255,.75)" }} />
        <span style={{ width: size * 0.11, height: size * 0.2, borderRadius: "50%", background: "#fff", boxShadow: "0 0 8px rgba(255,255,255,.75)" }} />
      </div>

      {/* Sparks — celebrating only */}
      <div className="lumiorb-sparks">
        <span className="lumiorb-spark lumiorb-spark-1" />
        <span className="lumiorb-spark lumiorb-spark-2" />
        <span className="lumiorb-spark lumiorb-spark-3" />
        <span className="lumiorb-spark lumiorb-spark-4" />
      </div>
    </div>
  );
}
